import React from "react";

import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import SelectTemplate from "@/components/SelectTemplate";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/context/user-context";
import { Entity, EntityType } from "@/queries";

interface TemplateSelectorProps {
  template?: Partial<Entity<EntityType.PROMPT | EntityType.SNIPPET>>;
  onSelect: (entity: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => void;
  isLoading?: boolean;
  onEditPrompt?: () => void;
  hasError?: boolean;
  errorMessage?: string;
}

const TemplateSelector = ({
  template,
  onSelect,
  isLoading = false,
  onEditPrompt,
  hasError,
  errorMessage,
}: TemplateSelectorProps) => {
  const isReadonly = useIsReadonly();
  const userContext = useUser();
  const workspaceId = userContext.activeWorkspaceId;

  const templateUrl = React.useMemo(() => {
    if (!template?.id || !workspaceId) {
      return undefined;
    }

    let url = `/workspace/${workspaceId}/prompt/${template.id}`;

    if (template.version_number) {
      url += `/version/${template.version_number}`;
    }

    return url;
  }, [template?.id, template?.version_number, workspaceId]);

  if (isLoading) {
    return <Skeleton height={40} className="w-full" />;
  }

  return (
    <ValidatedField path="configuration.template.name" variant="sources">
      <SelectTemplate
        template={template}
        onSelect={onSelect}
        readonly={isReadonly}
        selectLatest={false}
        onEditPrompt={onEditPrompt}
        hasError={hasError}
        errorMessage={errorMessage}
        url={templateUrl}
      />
    </ValidatedField>
  );
};

export default TemplateSelector;
